import { useEffect, useRef, useState } from 'react';

import type {
  AIPresenceState,
  PresenceSpeechEnvelope,
  PresenceTrackRef,
} from '@/common/components/presence/types';

export type { PresenceSpeechEnvelope };

const ZERO_ENVELOPE: PresenceSpeechEnvelope = { amp: 0, punch: 0 };

/** True when a live agent audio track should drive the speech envelope. */
export function isPresenceSpeechEnvelopeActive(
  state: AIPresenceState,
  mediaStreamTrack?: MediaStreamTrack | null,
): boolean {
  if (!mediaStreamTrack) return false;
  if (state === 'speaking') return true;
  // Agent state can lag behind audible TTS — treat thinking + track as speaking.
  if (state === 'thinking') return true;
  return false;
}

/** Prefer speaking visuals when reactive punch proves audio is playing. */
export function resolvePresenceVisualState(
  state: AIPresenceState,
  punch: number,
): AIPresenceState {
  if (state === 'thinking' && punch > 0) return 'speaking';
  return state;
}

/**
 * Audio-reactive speech envelope (amp + peak-hold punch) for the brand glow layer.
 * Ported from aurora amp/punch logic; frozen when reduced motion or no live track.
 */
export function usePresenceSpeechEnvelope(params: {
  state: AIPresenceState;
  trackRef?: PresenceTrackRef;
  reducedMotion: boolean;
  envelopeOverride?: PresenceSpeechEnvelope;
}): PresenceSpeechEnvelope {
  const {
    state, trackRef, reducedMotion, envelopeOverride,
  } = params;
  const [envelope, setEnvelope] = useState<PresenceSpeechEnvelope>(ZERO_ENVELOPE);
  const ampRef = useRef(0);
  const punchRef = useRef(0);
  const stateRef = useRef(state);

  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  const mediaStreamTrack = trackRef?.publication?.track?.mediaStreamTrack;
  const trackId = mediaStreamTrack?.id ?? null;
  const envelopeActive = isPresenceSpeechEnvelopeActive(state, mediaStreamTrack);
  const skipAnalyser = Boolean(envelopeOverride);

  useEffect(() => {
    if (reducedMotion || skipAnalyser || !envelopeActive || !mediaStreamTrack) {
      ampRef.current = 0;
      punchRef.current = 0;
      setEnvelope(ZERO_ENVELOPE);
      return undefined;
    }

    if (typeof window === 'undefined') {
      return undefined;
    }

    const AC = window.AudioContext
      || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    const audioCtx = new AC();
    const source = audioCtx.createMediaStreamSource(new MediaStream([mediaStreamTrack]));
    const analyser = audioCtx.createAnalyser();
    analyser.fftSize = 512;
    analyser.smoothingTimeConstant = 0.6;
    source.connect(analyser);
    const buffer = new Uint8Array(analyser.frequencyBinCount);
    let raf = 0;

    const tick = () => {
      if (!isPresenceSpeechEnvelopeActive(stateRef.current, mediaStreamTrack)) {
        ampRef.current = 0;
        punchRef.current = 0;
        setEnvelope(ZERO_ENVELOPE);
        raf = requestAnimationFrame(tick);
        return;
      }

      analyser.getByteFrequencyData(buffer);
      let sum = 0;
      const cutoff = Math.floor(buffer.length * 0.6);
      for (let i = 0; i < cutoff; i += 1) sum += buffer[i];
      const avg = sum / cutoff / 255;
      const ampRaw = ampRef.current * 0.45 + avg * 0.55;
      ampRef.current = ampRaw;
      const amp = Math.min(1, ampRaw * 1.6);
      punchRef.current = Math.max(amp, punchRef.current * 0.93);
      setEnvelope({ amp, punch: punchRef.current });
      raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      try {
        source.disconnect();
      } catch {
        /* ignore */
      }
      audioCtx.close();
    };
  }, [reducedMotion, skipAnalyser, envelopeActive, trackId, mediaStreamTrack]);

  if (reducedMotion) return ZERO_ENVELOPE;
  if (envelopeOverride) return envelopeOverride;
  return envelope;
}
